// Company logo helper — best-effort logo URL from Clearbit's free public endpoint.
// Clearbit Logo API:  https://logo.clearbit.com/{domain}  (no key, returns 404 for unknown brands)
//
// We map common stock tickers + reseller brand names → their public domain so
// e.g. "Apple" / "AAPL" → logo.clearbit.com/apple.com.
//
// When Clearbit returns 404, the calling component (TrendCard) catches via
// `onError` and falls back to a CSS-generated initial avatar.

// Tickers/names we know well — explicit > guesswork
const EXPLICIT_DOMAINS = {
  // Big tech
  apple: 'apple.com',         aapl: 'apple.com',
  microsoft: 'microsoft.com', msft: 'microsoft.com',
  alphabet: 'google.com',     googl: 'google.com', goog: 'google.com', google: 'google.com',
  meta: 'meta.com',           fb: 'meta.com', facebook: 'meta.com',
  amazon: 'amazon.com',       amzn: 'amazon.com',
  tesla: 'tesla.com',         tsla: 'tesla.com',
  nvidia: 'nvidia.com',       nvda: 'nvidia.com',
  netflix: 'netflix.com',     nflx: 'netflix.com',
  oracle: 'oracle.com',       orcl: 'oracle.com',
  // Finance
  jpmorgan: 'jpmorganchase.com', jpm: 'jpmorganchase.com',
  goldmansachs: 'goldmansachs.com', gs: 'goldmansachs.com',
  visa: 'visa.com',           v: 'visa.com',
  mastercard: 'mastercard.com', ma: 'mastercard.com',
  // Consumer
  nike: 'nike.com',           nke: 'nike.com',
  adidas: 'adidas.com',       'adidas ag': 'adidas.com',
  unilever: 'unilever.com',   ul: 'unilever.com',
  procterandgamble: 'pg.com', 'procter & gamble': 'pg.com', pg: 'pg.com',
  reckitt: 'reckitt.com',     'reckitt benckiser': 'reckitt.com', rkt: 'reckitt.com',
  pepsico: 'pepsi.com',       pep: 'pepsi.com',
  cocacola: 'coca-cola.com',  ko: 'coca-cola.com', 'coca-cola': 'coca-cola.com',
  // UK
  bp: 'bp.com',
  shell: 'shell.com',
  hsbc: 'hsbc.com',
  vodafone: 'vodafone.com',
  // Crypto
  bitcoin: 'bitcoin.org',     btc: 'bitcoin.org',
  ethereum: 'ethereum.org',   eth: 'ethereum.org',
  // Sneakers / luxury
  jordan: 'jordan.com',
  yeezy: 'adidas.com',
  supreme: 'supremenewyork.com',
  louisvuitton: 'louisvuitton.com', 'louis vuitton': 'louisvuitton.com',
  gucci: 'gucci.com',
  rolex: 'rolex.com',
  hermes: 'hermes.com',
  // MGM
  mgm: 'mgmresorts.com', 'mgm resorts': 'mgmresorts.com',
};

function normalize(name) {
  return (name || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9 ]/g, '')
    .trim();
}

/**
 * Resolve a Clearbit logo URL for a given entity. Returns null when we can't
 * confidently produce a domain (caller should render the initial-avatar).
 */
export function logoUrlFor({ name, ticker } = {}) {
  if (!name && !ticker) return null;
  const candidates = [];
  if (ticker) candidates.push(normalize(ticker));
  if (name) {
    const n = normalize(name);
    candidates.push(n);
    // First word of the name (e.g. "Reckitt Benckiser" → "reckitt")
    const firstWord = n.split(' ')[0];
    if (firstWord && firstWord !== n) candidates.push(firstWord);
    // Compact form (no spaces) — handles "Procter And Gamble" → "procterandgamble"
    candidates.push(n.replace(/\s+/g, ''));
  }
  for (const c of candidates) {
    if (EXPLICIT_DOMAINS[c]) return `https://logo.clearbit.com/${EXPLICIT_DOMAINS[c]}?size=200`;
  }
  // Fallback heuristic: try `<firstword>.com` via Clearbit; Clearbit returns 404
  // for unknown brands which the caller catches via onError.
  if (name) {
    const fw = normalize(name).split(' ')[0];
    if (fw && fw.length >= 2) return `https://logo.clearbit.com/${fw}.com?size=200`;
  }
  return null;
}

/**
 * Currency / forex / crypto symbol fallback for short tickers (May 2026 iter 57).
 * Returns a single glyph (£/$/€/₿/Ξ/¥/₹) if the entity name is a known currency
 * code, otherwise null. Used by TrendCard to render a more meaningful initial
 * avatar than the bare first letter (e.g. EUR/GBP → £ rather than "E").
 */
const CURRENCY_GLYPHS = {
  usd: '$', us$: '$', usdt: '$', usdc: '$',
  gbp: '£', gb: '£',
  eur: '€', euro: '€',
  jpy: '¥', yen: '¥', cny: '¥', rmb: '¥',
  inr: '₹', rupee: '₹',
  aud: 'A$', cad: 'C$', nzd: 'NZ$', chf: '₣',
  btc: '₿', bitcoin: '₿',
  eth: 'Ξ', ethereum: 'Ξ',
  ltc: 'Ł', xrp: '✕', sol: '◎', ada: '₳',
  doge: 'Ð', dot: '●', avax: '🜁', matic: '◆',
};

export function symbolForName(name = '') {
  if (!name) return null;
  // Forex pairs like "EUR/GBP" → take the first half before the slash so
  // we map to the leading currency's glyph.
  const head = name.split(/[\/\\\-:· ]/)[0].toLowerCase().trim();
  if (CURRENCY_GLYPHS[head]) return CURRENCY_GLYPHS[head];
  // Also try the whole name (no separator) — e.g. "USD" → $.
  const full = name.toLowerCase().trim();
  if (CURRENCY_GLYPHS[full]) return CURRENCY_GLYPHS[full];
  return null;
}

/**
 * Stable accent colour for a brand initial — derived from a tiny string hash so
 * the same brand always gets the same colour across renders/sessions.
 */
const PALETTE = [
  '#3B82F6', '#FACC15', '#06B6D4', '#10B981', '#A855F7',
  '#F97316', '#EC4899', '#EF4444', '#14B8A6', '#8B5CF6',
];
export function colorForName(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}
